/**
 * 事实包页面 — FactPacks（F6，对齐概念稿 fact-packs.html）。
 *
 * GET /api/products/{product_id}/fact-packs  版本列表（按 finalized_at 倒序）
 * GET /api/fact-packs/{pack_id}              条目明细（按 role 裁剪）
 *
 * 结构：g38（左版本列表卡 + 客户视图说明 / 右条目表）。
 * 网站为只读镜像，提炼与会签在 geo-fact-pack skill 侧完成（FR1）；
 * 「仅内部」条目对客户查看者不可见（接口侧过滤，PRD Q8 / FR2）。
 * 后端端点并行开发中：全部按契约盲写 + EmptyState v2 降级，不红屏。
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
} from "@mui/material";
import PageHeader from "../components/PageHeader";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import { getFactPacks, getFactPackDetail } from "../api/factPacks";
import { getProductDetail } from "../api/products";
import { useCompanyStore } from "../store/companyStore";
import {
  FACT_AUTH_MAP,
  FACT_LEVEL_MAP,
  FACT_PACK_STATUS_MAP,
} from "../utils/constants";
import { formatDate, formatDateTime } from "../utils/format";
import type { FactItem, FactPack } from "../types";

/** 字典徽章（未命中回退灰）。 */
function DictBadge({
  map,
  value,
  fallback,
}: {
  map: Record<string, { label: string; color: string; bg: string }>;
  value?: string;
  fallback?: string;
}) {
  const info = (value && map[value]) || {
    label: value || fallback || "—",
    color: "text.secondary",
    bg: "semanticBg.neutral",
  };
  return <Badge label={info.label} color={info.color} bgColor={info.bg} />;
}

export default function FactPacks() {
  const { productId } = useParams<{ productId: string }>();
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);
  const effectiveProductId = productId || selectedProductId;

  const [productName, setProductName] = useState("");
  const [packs, setPacks] = useState<FactPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const [selectedPackId, setSelectedPackId] = useState("");
  const [items, setItems] = useState<FactItem[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailFailed, setDetailFailed] = useState(false);

  /** 产品名（标题用，失败静默）。 */
  useEffect(() => {
    if (!effectiveProductId) return;
    getProductDetail(effectiveProductId)
      .then((d) => setProductName(d.product?.name || ""))
      .catch(() => setProductName(""));
  }, [effectiveProductId]);

  /** 版本列表。 */
  useEffect(() => {
    if (!effectiveProductId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setFailed(false);
    getFactPacks(effectiveProductId)
      .then((res) => {
        const list = res.items || [];
        setPacks(list);
        // 默认选中第一个定稿版（列表按 finalized_at 倒序，items[0] 即当前版本）
        const current = list.find((p) => p.status === "finalized") || list[0];
        setSelectedPackId(current ? current.id : "");
      })
      .catch(() => {
        setPacks([]);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, [effectiveProductId]);

  /** 条目明细。 */
  useEffect(() => {
    if (!selectedPackId) {
      setItems([]);
      return;
    }
    setDetailLoading(true);
    setDetailFailed(false);
    getFactPackDetail(selectedPackId)
      .then((res) =>
        setItems(
          [...(res.items || [])].sort(
            (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
          ),
        ),
      )
      .catch(() => {
        setItems([]);
        setDetailFailed(true);
      })
      .finally(() => setDetailLoading(false));
  }, [selectedPackId]);

  const currentPack = packs.find((p) => p.id === selectedPackId);

  const columns: DataTableColumn<FactItem>[] = [
    {
      key: "statement",
      label: "事实陈述",
      render: (row) => (
        <Typography sx={{ fontSize: 13, fontWeight: 500, whiteSpace: "normal" }}>
          {row.statement}
        </Typography>
      ),
    },
    {
      key: "level",
      label: "级别",
      align: "center",
      render: (row) => <DictBadge map={FACT_LEVEL_MAP} value={row.level} />,
    },
    {
      key: "authorization",
      label: "授权状态",
      align: "center",
      // customer_viewer 接口不返回 authorization（只展示内容本身，PRD Q8）
      render: (row) =>
        row.authorization ? (
          <DictBadge map={FACT_AUTH_MAP} value={row.authorization} />
        ) : (
          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>—</Typography>
        ),
    },
    {
      key: "source",
      label: "来源",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.source || "—"}
        </Typography>
      ),
    },
    {
      key: "finalized_at",
      label: "入库时间",
      align: "right",
      render: () => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {formatDateTime(currentPack?.finalized_at)}
        </Typography>
      ),
    },
  ];

  if (!effectiveProductId) {
    return (
      <Box>
        <PageHeader
          title="事实包"
          breadcrumbs={[{ label: "资产管理" }, { label: "产品" }, { label: "事实包" }]}
        />
        <Card>
          <CardContent>
            <EmptyState
              text="请先选择一个产品"
              subtitle="从产品列表进入，或使用页面顶部的产品选择器"
            />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={productName ? `事实包 · ${productName}` : "事实包"}
        breadcrumbs={[
          { label: "资产管理" },
          { label: "产品", path: "/products" },
          ...(productName
            ? [{ label: productName, path: `/products/${effectiveProductId}` }]
            : []),
          { label: "事实包" },
        ]}
        subtitle="对外内容的事实口径 · 网站只读镜像，提炼与会签在 geo-fact-pack skill 侧完成（FR1）"
      />

      {loading ? (
        <Loading fullPage />
      ) : failed ? (
        <Card>
          <CardContent>
            <EmptyState
              text="事实包暂未接入"
              source="由 geo-fact-pack skill 产出 · 端点未就绪时显示空态"
              hint="定稿入库后自动展示版本列表与条目"
            />
          </CardContent>
        </Card>
      ) : packs.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              text="暂无事实包版本"
              source="由 geo-fact-pack skill 产出 · 定稿入库后自动展示"
            />
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {/* 左：版本列表 + 客户视图说明（g38 的 3） */}
          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>版本列表</Typography>
                  <Badge label="仅定稿版" />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {packs.map((p, idx) => {
                    const isCurrent = idx === 0;
                    const isSelected = p.id === selectedPackId;
                    return (
                      <Box
                        key={p.id}
                        onClick={() => setSelectedPackId(p.id)}
                        sx={{
                          border: "1px solid",
                          borderColor: isSelected ? "primary.main" : "divider",
                          borderRadius: 2,
                          p: 1.5,
                          cursor: "pointer",
                          bgcolor: isSelected ? "brand.50" : "background.paper",
                          "&:hover": { bgcolor: "brand.50" },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                            {p.version}
                          </Typography>
                          {isCurrent && (
                            <Badge label="当前" color="primary.main" bgColor="brand.50" />
                          )}
                          <DictBadge map={FACT_PACK_STATUS_MAP} value={p.status} />
                        </Box>
                        <Typography
                          sx={{ fontSize: 12, color: "text.secondary", mt: 0.75, lineHeight: 1.8 }}
                        >
                          条目 {p.item_count} 条
                          {p.level_a_count !== undefined &&
                            `（A 级 ${p.level_a_count}${p.level_b_count !== undefined ? ` / B 级 ${p.level_b_count}` : ""}）`}
                          <br />
                          入库 {formatDateTime(p.finalized_at)}
                          {p.produced_by && (
                            <>
                              <br />
                              产出方式：{p.produced_by}
                            </>
                          )}
                          {p.fresh_until && (
                            <>
                              <br />
                              保鲜期至 {formatDate(p.fresh_until)}
                            </>
                          )}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.secondary",
                    lineHeight: 1.8,
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: "1px solid",
                    borderColor: "lineSoft",
                  }}
                >
                  保鲜期到期前 30 天列表徽标提醒，到期标「待复审」；版本
                  diff 一期提供并排手动对照（FR3）。
                </Typography>
              </CardContent>
            </Card>

            <Alert severity="info">
              「仅内部」条目（任何级别）对客户查看者不可见；「可署名 /
              匿名」条目可用于对外文案，客户视图隐藏授权标识、只展示内容本身（PRD Q8 / FR2）。
            </Alert>
          </Grid>

          {/* 右：条目表（g38 的 8） */}
          <Grid item xs={12} md={8}>
            <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {currentPack
                  ? `${currentPack.version} · 事实条目 ${currentPack.item_count} 条`
                  : "事实条目"}
              </Typography>
              {currentPack?.level_a_count !== undefined && (
                <Badge label={`A 级 ${currentPack.level_a_count}`} variant="success" />
              )}
              {currentPack?.level_b_count !== undefined && (
                <Badge label={`B 级 ${currentPack.level_b_count}`} variant="info" />
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                按授权状态控制可见性
              </Typography>
            </Box>
            {detailLoading ? (
              <Loading text="正在加载条目..." />
            ) : detailFailed ? (
              <Card>
                <CardContent>
                  <EmptyState
                    text="条目明细暂未接入"
                    source="由 geo-fact-pack skill 产出 · 端点未就绪时显示空态"
                  />
                </CardContent>
              </Card>
            ) : (
              <DataTable
                columns={columns}
                data={items}
                showPagination={false}
                getRowId={(row) => row.id}
                emptyText="该版本暂无条目"
              />
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
